import { useMemo } from 'react';
import { theme } from '@frontend/theme';
import { SkeletonLoader, SkeletonLoaders } from '@frontend/design-system';

type Props = {
  recipients?: number;
};

export const AudienceTraySkeleton = ({ recipients: rows = 10 }: Props) => {
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          css={{
            display: 'grid',
            gridTemplateColumns: '24px 1fr',
            alignItems: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(2),
            borderBottom: `1px solid ${theme.colors.neutral10}`,
            '&:last-child': {
              borderBottom: 'none',
            },
          }}
        >
          {/* Checkbox */}
          <SkeletonLoader width={theme.spacing(3)} height={theme.spacing(3)} />

          {/* Recipient */}
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
            }}
          >
            <SkeletonLoader width={`${Math.floor(Math.random() * 21) + 15}%`} height={theme.spacing(2)} />
            <SkeletonLoader width='20%' height={theme.spacing(1.5)} />
          </div>
        </li>
      )),
    [rows]
  );

  return (
    <SkeletonLoaders isLoading={true}>
      <aside
        css={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <section
          css={{
            flexGrow: 1,
            padding: theme.spacing(4),
            overflow: 'hidden',
          }}
        >
          <header>
            {/* Header Label */}
            <SkeletonLoaders.Loader width={theme.spacing(27)} height={theme.spacing(4)} />

            {/* Close Button */}
            <SkeletonLoaders.Loader
              shape='circle'
              height={theme.spacing(4)}
              width={theme.spacing(4)}
              css={{
                position: 'absolute',
                top: theme.spacing(4),
                right: theme.spacing(4),
              }}
            />
          </header>

          {/* Tabs */}
          <nav
            css={{
              marginTop: theme.spacing(4),
              display: 'flex',
              justifyContent: 'space-between',
              padding: theme.spacing(0, 9, 1, 9),
              borderBottom: `2px solid ${theme.colors.neutral10}`,
            }}
          >
            <SkeletonLoaders.Loader width={theme.spacing(15)} height={theme.spacing(3)} />
            <SkeletonLoaders.Loader width={theme.spacing(15)} height={theme.spacing(3)} />
          </nav>

          {/* Search and Filter */}
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 32px',
              gap: theme.spacing(2),
              alignItems: 'center',
              marginTop: theme.spacing(2),
            }}
          >
            <SkeletonLoaders.Loader height={theme.spacing(5)} />
            <SkeletonLoaders.Loader shape='circle' width={theme.spacing(4)} height={theme.spacing(4)} />
          </div>

          {/* Select All Label */}
          <SkeletonLoaders.Loader
            width={theme.spacing(11)}
            height={theme.spacing(2)}
            css={{ margin: theme.spacing(2, 1, 0, 1) }}
          />

          {/* Recipients List */}
          <ul
            css={{
              listStyle: 'none',
              padding: 0,
              marginTop: theme.spacing(1),
            }}
          >
            {skeletonRows}
          </ul>
        </section>

        {/* Footer */}
        <footer
          css={{
            borderTop: `2px solid ${theme.colors.neutral10}`,
            padding: theme.spacing(2, 4, 4, 4),
          }}
        >
          {/* Count Label */}
          <div
            css={{
              display: 'flex',
              justifyContent: 'end',
              marginBottom: theme.spacing(3),
            }}
          >
            <SkeletonLoader width={theme.spacing(18)} height={theme.spacing(4)} />
          </div>

          {/* Action Buttons */}
          <div
            css={{
              display: 'flex',
              justifyContent: 'end',
              gap: theme.spacing(1),
            }}
          >
            <SkeletonLoader width={theme.spacing(9)} height={theme.spacing(5)} />
            <SkeletonLoader width={theme.spacing(16)} height={theme.spacing(5)} />
          </div>
        </footer>
      </aside>
    </SkeletonLoaders>
  );
};
